import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, hasRole } from "@/lib/auth";
import { api } from "@/lib/api";
import { Users, Building2, AlertTriangle, ChevronRight, Activity, Heart, BarChart3, Target, ClipboardList, HeartHandshake, Sparkles, Trophy, Plus } from "lucide-react";
import StatCard from "@/components/StatCard";
import RoleBadge from "@/components/RoleBadge";
import ManagerVelocityWidget from "@/components/ManagerVelocityWidget";
import HomeBrandBanner from "@/components/HomeBrandBanner";

export default function Home() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let m = true;
    api
      .get("/stats/home")
      .then((r) => m && setStats(r.data))
      .finally(() => m && setLoading(false));
    return () => {
      m = false;
    };
  }, []);

  return (
    <div className="space-y-8 max-w-7xl" data-testid="home-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">Welcome back</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">
            {greet()}, <span className="text-slate-500 font-medium">{user?.email?.split("@")[0]}</span>.
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            Your team's performance home &mdash; goals, check-ins, reviews, and the career story of every employee.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="home-stats-grid">
        <StatCard
          label="My objectives"
          value={loading ? "—" : stats?.my_objective_count ?? 0}
          hint={stats?.active_cycles ? `${stats.active_cycles} active cycle${stats.active_cycles === 1 ? "" : "s"}` : "in active cycles"}
          icon={Target}
          accent="bg-violet-50 text-violet-700"
          data-testid="stat-my-objectives"
        />
        <StatCard
          label="Self-reviews due"
          value={loading ? "—" : stats?.pending_self_reviews ?? 0}
          hint="awaiting your input"
          icon={ClipboardList}
          accent="bg-amber-50 text-amber-700"
          data-testid="stat-self-reviews"
        />
        <StatCard
          label="Awaiting your review"
          value={loading ? "—" : (stats?.pending_decisions ?? 0) + (stats?.pending_manager_reviews ?? 0)}
          hint="objectives + manager reviews"
          icon={ClipboardList}
          accent="bg-blue-50 text-blue-700"
          data-testid="stat-pending-decisions"
        />
        <StatCard
          label="360 to fill"
          value={loading ? "—" : stats?.pending_360_inbox ?? 0}
          hint="peer feedback requests"
          icon={Sparkles}
          accent="bg-slate-900 text-white"
          data-testid="stat-360-inbox"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="home-people-grid">
        <StatCard label="Total people" value={loading ? "—" : stats?.total_employees ?? 0} hint="in your workspace" icon={Users} accent="bg-slate-100 text-slate-700" data-testid="stat-total-employees" />
        <StatCard label="Active" value={loading ? "—" : stats?.active_employees ?? 0} hint="currently on the roster" icon={Activity} accent="bg-emerald-50 text-emerald-700" data-testid="stat-active-employees" />
        <StatCard label="On leave" value={loading ? "—" : stats?.on_leave ?? 0} hint="includes sabbatical" icon={Heart} accent="bg-amber-50 text-amber-700" data-testid="stat-on-leave" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Departments */}
        <section className="axia-card p-6 lg:col-span-1" data-testid="card-departments">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-semibold text-slate-900">By department</h2>
            <Building2 className="w-4 h-4 text-slate-400" />
          </div>
          <div className="mt-4 space-y-3">
            {(stats?.by_department || []).map((d) => {
              const max = Math.max(...(stats?.by_department || []).map((x) => x.count), 1);
              const pct = Math.round((d.count / max) * 100);
              return (
                <div key={d.department} data-testid={`dept-row-${d.department}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-slate-700">{d.department}</span>
                    <span className="font-mono text-slate-500">{d.count}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-slate-900" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {(!stats?.by_department || stats.by_department.length === 0) && (
              <div className="text-sm text-slate-400">No departments yet.</div>
            )}
          </div>
        </section>

        {/* Recent kudos */}
        <section className="axia-card p-6 lg:col-span-1" data-testid="card-kudos">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-semibold text-slate-900">Recent kudos</h2>
            <Link to="/recognition" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              wall <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <ul className="mt-4 space-y-3">
            {(stats?.recent_kudos || []).map((k) => (
              <li key={k.id} className="text-sm" data-testid={`home-kudos-${k.id}`}>
                <div className="text-xs text-slate-500">
                  <span className="font-medium text-slate-700">{k.giver_name}</span>
                  <span> → </span>
                  <span className="font-medium text-slate-700">{k.recipient_name}</span>
                </div>
                <p className="text-[13px] text-slate-700 leading-snug mt-0.5 line-clamp-2">{k.message}</p>
              </li>
            ))}
            {(!stats?.recent_kudos || stats.recent_kudos.length === 0) && (
              <li className="text-sm text-slate-400">No kudos yet — start the recognition flywheel.</li>
            )}
          </ul>
        </section>

        {/* Recent lifecycle events */}
        <section className="axia-card p-6 lg:col-span-1" data-testid="card-recent-events">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-semibold text-slate-900">Lifecycle</h2>
            <Link to="/org" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              org <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <ul className="mt-4 divide-y divide-slate-100">
            {(stats?.recent_events || []).slice(0, 6).map((e) => (
              <li key={e.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`recent-event-${e.event_type}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-slate-900" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium capitalize text-slate-800 text-xs">{(e.event_type || "").replace(/_/g, " ")}</span>
                  <span className="text-slate-500 text-xs ml-2">{e.event_date}</span>
                </div>
              </li>
            ))}
            {(!stats?.recent_events || stats.recent_events.length === 0) && (
              <li className="py-3 text-sm text-slate-400">No events yet.</li>
            )}
          </ul>
        </section>
      </div>

      {hasRole(user, "tenant_admin") && <HomeBrandBanner />}

      <RecentWinsWidget />

      {hasRole(user, "manager") && <ManagerVelocityWidget />}

      {hasRole(user, "tenant_admin", "hr_admin", "hrbp") && (
        <section className="axia-card p-6 border-l-4 border-l-amber-400" data-testid="admin-quick-actions">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900">Bring your team into Axia</div>
              <p className="text-sm text-slate-500 mt-0.5">
                Bulk-import your employee master to populate org chart, lifecycle events, and (optionally) historical ratings on Day 1.
              </p>
              <div className="flex gap-3 mt-3">
                <Link
                  to="/admin/import"
                  className="inline-flex items-center h-9 px-3 bg-slate-900 text-white text-sm rounded-md hover:bg-slate-800"
                  data-testid="admin-go-import"
                >
                  Bulk import
                </Link>
                <Link
                  to="/admin/users"
                  className="inline-flex items-center h-9 px-3 border border-slate-200 text-sm text-slate-700 rounded-md hover:bg-slate-50"
                  data-testid="admin-go-users"
                >
                  Invite users
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function greet() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function RecentWinsWidget() {
  const [wins, setWins] = React.useState([]);
  const [suggestions, setSuggestions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let m = true;
    Promise.all([
      api.get("/wins", { params: { limit: 4 } }).catch(() => ({ data: { items: [] } })),
      api.get("/wins/suggestions/me").catch(() => ({ data: { items: [] } })),
    ]).then(([w, s]) => {
      if (!m) return;
      setWins(w.data.items || []);
      setSuggestions((s.data.items || []).slice(0, 3));
    }).finally(() => m && setLoading(false));
    return () => { m = false; };
  }, []);
  if (loading) return null;
  return (
    <section className="axia-card p-6" data-testid="home-wins-widget">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" />
          <h2 className="font-display text-base font-semibold text-slate-900">My recent wins</h2>
        </div>
        <Link to="/wins" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="home-wins-open">
          journal <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <div className="space-y-2">
          {wins.length === 0 ? (
            <Link to="/wins" className="block text-sm text-slate-500 italic hover:text-slate-900 px-3 py-3 rounded-md border border-dashed border-slate-200 hover:border-slate-300" data-testid="home-wins-empty-cta">
              <Plus className="w-3 h-3 inline mr-1" /> Log your first win — review-time you will thank you.
            </Link>
          ) : (
            wins.slice(0, 4).map((w) => (
              <Link key={w.id} to="/wins" className="block text-sm hover:bg-slate-50 rounded px-2 py-1.5 transition" data-testid={`home-win-${w.id}`}>
                <div className="text-[11px] text-slate-400 font-mono">{w.occurred_at}</div>
                <div className="font-medium text-slate-800 line-clamp-1">{w.title}</div>
              </Link>
            ))
          )}
        </div>
        <div>
          {suggestions.length > 0 ? (
            <>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-2">Capture these</div>
              <ul className="space-y-1.5">
                {suggestions.map((s) => (
                  <li key={`${s.source}-${s.source_ref_id}`}>
                    <Link to="/wins" className="block text-xs text-slate-700 hover:text-slate-900 bg-amber-50/40 hover:bg-amber-50 border border-amber-100 rounded p-2 transition" data-testid={`home-suggest-${s.source_ref_id}`}>
                      <span className="font-medium line-clamp-1">{s.title_suggestion}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="text-xs text-slate-400 italic">No nudges right now — keep capturing wins as they happen.</div>
          )}
        </div>
      </div>
    </section>
  );
}
